/**
 * `/campaigns` — the studio's email newsletters: how each performed, the
 * editions on their way out, and who the studio's marketing can reach
 * (email-campaigns, 2026-10-17; email-newsletter-editions, 2026-10-18;
 * build-output/architecture/email-campaigns.md).
 *
 * Three tabs, the open one kept in `?tab=`:
 *
 *   - Newsletters — every send, mirrored hourly by the `email_campaign_tracker`
 *     cron, with its numbers as of 6 hours, 24 hours, 48 hours, 7 days and 14
 *     days after sending — exact at those hours however late the tracker read
 *     them — plus the lifetime totals, the clicks per link, the email itself,
 *     and the learnings the newsletter lane (or staff) drew from them. An
 *     edition Clavis sends goes out as one row per lifecycle and arm: its rows
 *     sit together, and the tiles count it once.
 *   - Editions — each edition's status, version and approval; each body as a
 *     member sees it (read in full, shown in a sandboxed frame); while it
 *     awaits approval or is approved, who it would go to right now; for an
 *     admin, Approve vN while it awaits approval; once sent, each lifecycle
 *     and arm at a checkpoint, and arm B against arm A on the edition's own
 *     metric. Since 2026-10-31 (email-newsletter-tag-audience) who it goes to
 *     by its picked tags, and for an admin: the tags themselves (picked as on
 *     `/newsletter`, saved as the next version, which clears the approval);
 *     the send — Send now or Schedule for a time within 14 days, unlocked
 *     only once the total it will reach is typed back, a test edition to the
 *     members tagged testing only; and the emergency stop beside it,
 *     Withdraw while it is scheduled, Stop sending while it sends. Since
 *     newsletter-edition-withdraw (2026-10-31), Withdraw edition for an
 *     edition never scheduled: it is cancelled for good, behind a confirm.
 *   - Audiences — every active member by lifecycle and goal, and how many the
 *     email newsletter and a WhatsApp marketing send can reach.
 *
 * NO BESPOKE ENDPOINT: every read and the approval go through
 * `dispatchAction` → `POST /api/dispatch/:action` as `human:staff` (`api: []`
 * in the page registry, the same posture as `/newsletter`):
 * `list_email_campaigns`, `get_email_campaign`, `list_newsletter_editions`,
 * `get_newsletter_edition`, `preview_email_newsletter`,
 * `approve_newsletter_edition`, `set_newsletter_edition_audience`,
 * `send_email_newsletter`, `cancel_newsletter_send`,
 * `withdraw_newsletter_edition` and `preview_marketing_audience`; the tag list is the dashboard's ungated
 * picker feed, `GET /api/lookups/tags`, as on `/newsletter`. Approve, the
 * tags, Send, its stop and Withdraw edition show only when `list_newsletter_editions` says
 * this viewer is an admin, and each action refuses anyone else regardless.
 * A send keeps every guard the connector's has: the version it names must be
 * the approved one, the per-lifecycle counts the preview showed must still
 * hold, and one request id per checked preview makes a double press replay,
 * never send twice.
 *
 * Rates are shares of delivered (bounces of sent; `lib/campaigns.ts`). Opens
 * are inflated by Apple Mail Privacy Protection, which opens mail on delivery,
 * so the table leads with clicks and unsubscribes.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  EMAIL_CAMPAIGN_CHECKPOINTS,
  type EmailCampaignCheckpointKey,
} from "@layer/canonical-data/email-campaigns";
import { NEWSLETTER_AUDIENCE_TAGS_MAX, NEWSLETTER_VARIANTS } from "@layer/canonical-data/email-newsletter";
import {
  MARKETING_GOALS,
  MARKETING_LIFECYCLES,
  type MarketingChannel,
  type MarketingExclusionReason,
  type MarketingLifecycle,
} from "@layer/canonical-data/marketing-audience";
import {
  NEWSLETTER_SUBSCRIBER_VIEWS,
  type NewsletterSubscriberView,
} from "@layer/canonical-data/newsletter-subscribers";
import { AudienceListPicker } from "../components/AudienceListPicker";
import { EmailPreviewModal } from "../components/EmailPreviewModal";
import type { SearchableSelectOption } from "../components/SearchableSelect";
import { api } from "../lib/api";
import {
  ACTIVE_DOT,
  AXIS_STROKE,
  AXIS_STYLE,
  CHART_HEIGHT,
  GRID_STROKE,
  LINE_CURSOR,
  TOOLTIP_CONTENT_STYLE,
} from "../lib/chart-theme";
import {
  GRID_METRICS,
  LEARNING_KIND_LABEL,
  campaignRates,
  checkpointCell,
  checkpointSeries,
  clickedLinkView,
  fmtPct,
  gridMetric,
  metricCount,
  orderCampaignRows,
  pooledRates,
  splitLearnings,
  syncLine,
  tabKeyTarget,
  type CampaignDetail,
  type CampaignLearning,
  type CampaignMetrics,
  type CampaignSummary,
  type CampaignsList,
  type CheckpointCell,
  type GridMetricKey,
} from "../lib/campaigns";
import { dispatchAction, generateRequestIdV7 } from "../lib/dispatch";
import {
  EXPERIMENT_WIN_CHANCE,
  GOAL_LABEL,
  LIFECYCLE_LABEL,
  PRIMARY_METRIC_LABEL,
  approvalText,
  audienceMatrix,
  audienceText,
  bodyKeyLabel,
  bodyText,
  campaignDisplayName,
  canPickTags,
  cancelKind,
  confirmCountsFromPreview,
  editionGrid,
  editionStatusView,
  editionsPollDelay,
  exclusionLabel,
  exclusionRows,
  experimentReadout,
  fmtChance,
  goalDataThin,
  poolNote,
  queuedNoticeEnds,
  sameTags,
  sampleNewsletterBody,
  scheduleInWindow,
  scheduleWindow,
  sendBlockedReason,
  showApproveButton,
  showSendPanel,
  showWithdrawEdition,
  typedTotalMatches,
  verdictText,
  type EditionTone,
  type ExperimentComparison,
  type MarketingAudiencePreview,
  type NewsletterEdition,
  type NewsletterEditionDetail,
  type NewsletterEditionPreview,
  type NewsletterEditionsList,
} from "../lib/newsletter-editions";
import {
  JOINED_VIA_HINT,
  JOINED_VIA_LABEL,
  SUBSCRIBER_VIEW_LABEL,
  appendSubscriberPage,
  newslettersCell,
  subscriberTiles,
  type NewsletterSubscriber,
  type NewsletterSubscribersInput,
  type NewsletterSubscribersList,
} from "../lib/newsletter-subscribers";
import { useCachedResource } from "../lib/resource-cache";
import {
  isoToZonedDatetimeInput,
  studioFormat,
  studioTimeZone,
  zonedDatetimeInputToIso,
} from "../lib/studio-tz";

/** The stat tiles pool the real sends of this many days. */
const TILE_WINDOW_DAYS = 90;
/**
 * Newsletters-tab rows read per visit, newest first. An edition is up to
 * eight rows (four lifecycles × two arms), so this keeps the 90 days the
 * tiles pool.
 */
const CAMPAIGN_ROWS_LIMIT = 200;
/** Editions read per visit, newest first. */
const EDITIONS_LIMIT = 50;
/** One edition's campaign rows: a lifecycle × arm each, read when its panel opens. */
const EDITION_ROWS_LIMIT = MARKETING_LIFECYCLES.length * NEWSLETTER_VARIANTS.length;
/** Subscribers per page of the Subscribers tab; "Load more" reads the next. */
const SUBSCRIBERS_PAGE = 100;
/** How long the Subscribers search waits after the last keystroke before it reads. */
const SUBSCRIBER_SEARCH_DEBOUNCE_MS = 300;
/** What a thrown dispatch (no answer at all) reads as. */
const UNREACHABLE = "Could not reach the server. Check the connection and reload.";

const TABS = [
  { value: "newsletters", label: "Newsletters" },
  { value: "editions", label: "Editions" },
  { value: "audiences", label: "Audiences" },
  { value: "subscribers", label: "Subscribers" },
] as const;
type Tab = (typeof TABS)[number]["value"];

function tabFrom(raw: string | null): Tab {
  return TABS.find((t) => t.value === raw)?.value ?? "newsletters";
}

interface CampaignsListInput {
  readonly include_tests: boolean;
  readonly include_drafts: boolean;
  readonly limit: number;
  readonly learnings_limit?: number;
  /** Only this newsletter edition's rows. */
  readonly edition_id?: string;
}

function fmtWhen(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return studioFormat({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(d);
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return studioFormat({ day: "2-digit", month: "short", year: "numeric" }).format(d);
}

const fmtCount = (v: number | null): string => (v === null ? "—" : v.toLocaleString("en-GB"));

/** `1 claim`, `3 claims`. */
const countOf = (n: number, noun: string): string => `${fmtCount(n)} ${noun}${n === 1 ? "" : "s"}`;

/** One checkpoint cell of the newsletters table: what it shows, and its hover text. */
function cellView(cell: CheckpointCell, metricLabel: string): { text: string; title: string; className: string } {
  switch (cell.state) {
    case "none":
      return { text: "—", title: "Not sent", className: "campaign-cell-none" };
    case "captured":
      return {
        text: fmtPct(cell.rate),
        title: `${metricLabel}: ${fmtCount(cell.count)} as of ${fmtWhen(cell.asOf)}`,
        className: "campaign-cell-captured",
      };
    case "upcoming":
      return { text: "…", title: `Taken after ${fmtWhen(cell.asOf)}`, className: "campaign-cell-pending" };
    case "capturing":
      return {
        text: "…",
        title: `Due ${fmtWhen(cell.asOf)}; the tracker takes it on its next hourly run`,
        className: "campaign-cell-pending",
      };
    case "retrying":
      return {
        text: "retrying",
        title: `${String(cell.attempts)} failed attempts${cell.errorCode === null ? "" : ` (${cell.errorCode})`}; it keeps trying every hour`,
        className: "campaign-cell-warn",
      };
    case "failed":
      return {
        text: "failed",
        title: `Gave up after ${String(cell.attempts)} attempts${cell.errorCode === null ? "" : ` (${cell.errorCode})`}`,
        className: "campaign-cell-error",
      };
  }
}

function statusTag(c: CampaignSummary): JSX.Element | null {
  if (c.is_test) return <span className="tag">Test</span>;
  if (c.sent_at === null) {
    return <span className="tag tag-warn">{c.scheduled_at === null ? "Draft" : `Scheduled ${fmtWhen(c.scheduled_at)}`}</span>;
  }
  return null;
}

function LearningItem({ l, showCampaign }: { readonly l: CampaignLearning; readonly showCampaign: boolean }): JSX.Element {
  return (
    <li className={`campaign-learning campaign-learning-${l.kind}${l.status === "superseded" ? " is-superseded" : ""}`}>
      <div className="campaign-learning-head">
        <span className="tag">{LEARNING_KIND_LABEL[l.kind]}</span>
        {l.audience_key !== null && <span className="tag">{l.audience_key}</span>}
        {l.confidence !== null && <span className="subtle">{l.confidence} confidence</span>}
        {l.status === "superseded" && <span className="subtle">superseded</span>}
      </div>
      <p className="campaign-learning-statement">{l.statement}</p>
      <div className="subtle">
        {fmtDay(l.created_at)}
        {showCampaign && l.campaign_name !== null ? ` · ${l.campaign_name}` : ""}
        {` · ${l.created_by}`}
      </div>
    </li>
  );
}

/** The rows of the detail panel's checkpoint grid. */
const DETAIL_ROWS: ReadonlyArray<{
  readonly label: string;
  readonly count: string;
  readonly rate: (m: CampaignMetrics | null) => number | null;
}> = [
  { label: "Delivered", count: "delivered", rate: () => null },
  { label: "Unique opens", count: "unique_opened", rate: (m) => campaignRates(m).openRate },
  { label: "Unique clicks", count: "unique_clicked", rate: (m) => campaignRates(m).clickRate },
  { label: "Unsubscribes", count: "unsubscribed", rate: (m) => campaignRates(m).unsubscribeRate },
  { label: "Bounces", count: "bounced", rate: (m) => campaignRates(m).bounceRate },
  { label: "Spam complaints", count: "complained", rate: (m) => campaignRates(m).complaintRate },
];

function DetailPanel({
  detail,
  onClose,
}: {
  readonly detail: CampaignDetail;
  readonly onClose: () => void;
}): JSX.Element {
  const c = detail.campaign;
  const name = campaignDisplayName(c);
  const [preview, setPreview] = useState(false);
  const series = useMemo(() => checkpointSeries(c), [c]);
  const links = useMemo(
    () => [...c.clicked_links].sort((a, b) => b.unique_clicks - a.unique_clicks || b.clicks - a.clicks),
    [c.clicked_links],
  );
  const columns = EMAIL_CAMPAIGN_CHECKPOINTS.map(({ key }) => ({
    key,
    metrics: c.checkpoints.find((k) => k.checkpoint === key && k.captured_at !== null)?.metrics ?? null,
  }));

  return (
    <section className="panel campaign-detail" aria-label={`Newsletter: ${name}`}>
      <div className="campaign-detail-head">
        <h2>{name}</h2>
        <div className="campaign-detail-actions">
          {c.html !== null && (
            <button type="button" className="link-button" onClick={() => setPreview(true)}>
              View the email
            </button>
          )}
          <button type="button" className="link-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <dl className="campaign-detail-facts">
        <dt>Subject</dt>
        <dd>{c.subject ?? "—"}</dd>
        <dt>Preview text</dt>
        <dd>{c.preview_text ?? "—"}</dd>
        <dt>From</dt>
        <dd>{c.from_address ?? "—"}</dd>
        <dt>Sent</dt>
        <dd>
          {fmtWhen(c.sent_at)}
          {c.recipient_count !== null ? ` to ${fmtCount(c.recipient_count)}` : ""}
        </dd>
      </dl>

      <h3>As of each checkpoint</h3>
      <div className="table-scroll-x">
        <table className="campaign-grid table-cards">
          <thead>
            <tr>
              <th />
              {columns.map((col) => (
                <th key={col.key} className="num">
                  {col.key}
                </th>
              ))}
              <th className="num">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            {DETAIL_ROWS.map((row) => (
              <tr key={row.label}>
                <td className="table-card-title">{row.label}</td>
                {columns.map((col) => (
                  <td key={col.key} data-label={col.key} className="num">
                    {fmtCount(metricCount(col.metrics, row.count))}
                    {row.rate(col.metrics) !== null && (
                      <span className="subtle campaign-rate">{fmtPct(row.rate(col.metrics), 2)}</span>
                    )}
                  </td>
                ))}
                <td data-label="Lifetime" className="num">
                  {fmtCount(metricCount(c.latest_metrics, row.count))}
                  {row.rate(c.latest_metrics) !== null && (
                    <span className="subtle campaign-rate">{fmtPct(row.rate(c.latest_metrics), 2)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="member-note subtle">
        Each column is exact as of that hour after sending. Rates are a share of delivered;
        bounces are a share of sent. Lifetime totals were last read {fmtWhen(c.latest_metrics_at)}.
      </p>

      {series.length >= 2 && (
        <>
          <h3>Clicks and unsubscribes, % of delivered</h3>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={[...series]} margin={{ top: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} stroke={GRID_STROKE} />
              <XAxis dataKey="checkpoint" tick={AXIS_STYLE} stroke={AXIS_STROKE} />
              <YAxis tick={AXIS_STYLE} stroke={AXIS_STROKE} width={36} unit="%" />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} cursor={LINE_CURSOR} />
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="clicks"
                name="Unique clicks"
                stroke="var(--c-signal-line-53)"
                strokeWidth={2}
                activeDot={ACTIVE_DOT}
              />
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="unsubscribes"
                name="Unsubscribes"
                stroke="var(--c-danger-text-41)"
                strokeWidth={1.5}
                activeDot={ACTIVE_DOT}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}

      <h3>Clicks per link</h3>
      {links.length === 0 ? (
        <p className="empty">
          {c.clicked_links_at === null ? "Not read from Resend yet." : "Nobody clicked a link."}
        </p>
      ) : (
        <div className="table-scroll-x">
          <table className="campaign-links table-cards">
            <thead>
              <tr>
                <th>Link</th>
                <th className="num">Unique clicks</th>
                <th className="num">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const view = clickedLinkView(l.url);
                return (
                  <tr key={l.url}>
                    <td className="table-card-title campaign-link-url">
                      {view.href === null ? (
                        view.text
                      ) : (
                        <a href={view.href} target="_blank" rel="noreferrer">
                          {view.text}
                        </a>
                      )}
                    </td>
                    <td data-label="Unique clicks" className="num">
                      {fmtCount(l.unique_clicks)}
                    </td>
                    <td data-label="Clicks" className="num">
                      {fmtCount(l.clicks)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3>Learnings from this newsletter</h3>
      {detail.learnings.length === 0 ? (
        <p className="empty">None recorded yet.</p>
      ) : (
        <ul className="campaign-learnings">
          {detail.learnings.map((l) => (
            <LearningItem key={l.learning_id} l={l} showCampaign={false} />
          ))}
        </ul>
      )}

      {preview && c.html !== null && (
        <EmailPreviewModal
          title={name}
          {...(c.preview_text !== null ? { subtitle: c.preview_text } : {})}
          {...(c.subject !== null ? { subject: c.subject } : {})}
          html={c.html}
          onClose={() => setPreview(false)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Newsletters tab.
// ---------------------------------------------------------------------------

function NewslettersTab(): JSX.Element {
  const [list, setList] = useState<CampaignsList | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [metricKey, setMetricKey] = useState<GridMetricKey>(GRID_METRICS[0].key);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const now = useMemo(() => new Date(), [list]);

  useEffect(() => {
    // Toggling "show tests and drafts" twice fires two reads; only the
    // latest one may land, or a late answer to the first shows the wrong
    // list under the toggle (2026-10-18 review).
    let cancelled = false;
    void dispatchAction<CampaignsListInput, CampaignsList>("list_email_campaigns", {
      include_tests: showHidden,
      include_drafts: showHidden,
      limit: CAMPAIGN_ROWS_LIMIT,
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setList(res.response);
          setListError(null);
        } else {
          setListError(res.message);
        }
      })
      .catch(() => {
        if (!cancelled) setListError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [showHidden]);

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    void dispatchAction<{ campaign_id: string }, CampaignDetail>("get_email_campaign", {
      campaign_id: selectedId,
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setDetail(res.response);
        else setDetailError(res.message);
      })
      .catch(() => {
        if (!cancelled) setDetailError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (detail !== null) detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [detail]);

  const metric = gridMetric(metricKey);
  const campaigns = list?.campaigns ?? [];
  const rows = useMemo(() => orderCampaignRows(campaigns), [campaigns]);
  const pooled = useMemo(() => pooledRates(campaigns, now, TILE_WINDOW_DAYS), [campaigns, now]);
  const learnings = useMemo(() => splitLearnings(list?.learnings ?? []), [list]);
  const imports = list?.unsubscribe_imports ?? null;

  return (
    <>
      <p className="member-note">
        Every email newsletter and how it did: its numbers 6 hours, 24 hours, 48 hours, 7 days and
        14 days after sending, its clicks per link, and the learnings drawn from it. Opens are
        inflated by Apple Mail, which opens mail on delivery, so clicks and unsubscribes are the
        numbers to trust.
      </p>
      {list !== null && <p className="subtle">{syncLine(list.sync, now)}</p>}
      {listError !== null && <div className="tag tag-error campaigns-notice">{listError}</div>}

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Newsletters · {TILE_WINDOW_DAYS}d</div>
          <div className="value">{list === null ? "—" : fmtCount(pooled.sends)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Delivered · {TILE_WINDOW_DAYS}d</div>
          <div className="value">{fmtCount(pooled.delivered)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Unique click rate</div>
          <div className="value">{fmtPct(pooled.clickRate)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Unsubscribe rate</div>
          <div className="value">{fmtPct(pooled.unsubscribeRate, 2)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Unique open rate</div>
          <div className="value">{fmtPct(pooled.openRate)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Bounce rate</div>
          <div className="value">{fmtPct(pooled.bounceRate, 2)}</div>
        </div>
        <div className="stat-card" title="Readers who unsubscribed through a newsletter's own link, now opted out of all marketing in Clavis">
          <div className="label">Resend unsubscribes applied</div>
          <div className="value">{imports === null ? "—" : fmtCount(imports.opted_out)}</div>
        </div>
      </div>

      <section className="panel">
        <h2>Newsletters</h2>
        <div className="campaigns-controls">
          <label>
            Checkpoints show{" "}
            <select value={metricKey} onChange={(e) => setMetricKey(gridMetric(e.target.value).key)}>
              {GRID_METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> Show
            test sends and drafts
          </label>
        </div>
        {list === null && listError === null && <p className="empty">Loading…</p>}
        {list !== null && campaigns.length === 0 && (
          <p className="empty">No newsletter yet. The tracker reads Resend every hour.</p>
        )}
        {rows.length > 0 && (
          <div className="table-scroll-x">
            <table className="campaigns-table table-cards">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>Newsletter</th>
                  <th className="num">Delivered</th>
                  {EMAIL_CAMPAIGN_CHECKPOINTS.map(({ key }) => (
                    <th key={key} className="num">
                      {key}
                    </th>
                  ))}
                  <th className="num">Lifetime</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const lifetime = metric.rate(campaignRates(c.latest_metrics));
                  const name = campaignDisplayName(c);
                  return (
                    <tr key={c.campaign_id} className={c.campaign_id === selectedId ? "is-selected" : undefined}>
                      <td data-label="Sent">{fmtWhen(c.sent_at)}</td>
                      <td className="table-card-title">
                        <button
                          type="button"
                          className="campaign-open"
                          aria-expanded={c.campaign_id === selectedId}
                          onClick={() => setSelectedId(c.campaign_id === selectedId ? null : c.campaign_id)}
                        >
                          {name}
                        </button>{" "}
                        {statusTag(c)}
                        {c.subject !== null && c.subject !== name && <div className="subtle">{c.subject}</div>}
                      </td>
                      <td data-label="Delivered" className="num">
                        {fmtCount(metricCount(c.latest_metrics, "delivered"))}
                      </td>
                      {EMAIL_CAMPAIGN_CHECKPOINTS.map(({ key }) => {
                        const v = cellView(checkpointCell(c, key, metric, now), metric.label);
                        return (
                          <td key={key} data-label={key} className={`num ${v.className}`} title={v.title}>
                            {v.text}
                          </td>
                        );
                      })}
                      <td data-label="Lifetime" className="num" title={`${metric.label}: ${fmtCount(metricCount(c.latest_metrics, metric.count))}`}>
                        {fmtPct(lifetime)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="member-note subtle">
          {metric.label} as a share of delivered, as of each checkpoint. Open a newsletter for every
          number, the clicks per link and the email itself.
        </p>
      </section>

      <div ref={detailRef}>
        {selectedId !== null && detail === null && detailError === null && (
          <section className="panel">
            <p className="empty">Loading the newsletter…</p>
          </section>
        )}
        {detailError !== null && (
          <section className="panel">
            <div className="tag tag-error campaigns-notice">{detailError}</div>
          </section>
        )}
        {detail !== null && <DetailPanel detail={detail} onClose={() => setSelectedId(null)} />}
      </div>

      <section className="panel">
        <h2>Learnings</h2>
        <p className="member-note subtle">
          What the sends have taught us. The newsletter lane records one after each analysis (the
          subject at 48 hours, clicks at 7 days, unsubscribes at 14 days); a newer learning can
          replace an older one.
        </p>
        {list !== null && learnings.active.length === 0 && <p className="empty">None recorded yet.</p>}
        {learnings.active.length > 0 && (
          <ul className="campaign-learnings">
            {learnings.active.map((l) => (
              <LearningItem key={l.learning_id} l={l} showCampaign />
            ))}
          </ul>
        )}
        {learnings.superseded.length > 0 && (
          <>
            <button type="button" className="link-button" onClick={() => setShowSuperseded((v) => !v)}>
              {showSuperseded ? "Hide" : "Show"} {String(learnings.superseded.length)} replaced
            </button>
            {showSuperseded && (
              <ul className="campaign-learnings">
                {learnings.superseded.map((l) => (
                  <LearningItem key={l.learning_id} l={l} showCampaign />
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Editions tab.
// ---------------------------------------------------------------------------

const TONE_CLASS: Readonly<Record<EditionTone, string>> = {
  warn: "tag tag-warn",
  ok: "tag tag-success",
  info: "tag",
  muted: "tag",
};

function sendText(e: NewsletterEdition): string {
  if (e.sent_at !== null) return `Sent ${fmtWhen(e.sent_at)}`;
  if (e.scheduled_for !== null) return `Scheduled ${fmtWhen(e.scheduled_for)}`;
  return "Not scheduled";
}

/** Who an edition reaches and why the rest are left out, per channel. */
function LeftOut({
  title,
  counts,
  channel,
}: {
  readonly title: string;
  readonly counts: Readonly<Record<MarketingExclusionReason, number>>;
  readonly channel: MarketingChannel;
}): JSX.Element {
  const rows = exclusionRows(counts, channel);
  return (
    <div className="campaign-leftout">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="empty">Nobody left out.</p>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.reason}>
              <span>{r.label}</span>
              <span className="num">{fmtCount(r.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecipientsSection({
  preview,
  error,
  isTest,
}: {
  readonly preview: NewsletterEditionPreview | null;
  readonly error: string | null;
  /** A test edition: it goes only to the active members tagged `testing`. */
  readonly isTest: boolean;
}): JSX.Element {
  const totals = preview?.lifecycles.reduce(
    (s, l) => ({ A: s.A + l.variants.A, B: s.B + l.variants.B }),
    { A: 0, B: 0 },
  );
  const goalRows =
    preview?.lifecycles.flatMap((l) =>
      l.goals.filter((g) => g.recipients > 0).map((g) => ({ lifecycle: l.lifecycle, goal: g })),
    ) ?? [];
  return (
    <>
      <h3>{isTest ? "Who this test edition would go to now" : "Who it would go to now"}</h3>
      {isTest && (
        <p className="member-note">
          This is a test edition: it goes only to members tagged testing, never to anyone else.
          Everyone else is counted below as not tagged testing.
        </p>
      )}
      {preview === null && error === null && <p className="empty">Counting…</p>}
      {error !== null && <div className="tag tag-error campaigns-notice">{error}</div>}
      {preview !== null && (
        <>
          <div className="table-scroll-x">
            <table className="campaign-recipients table-cards">
              <thead>
                <tr>
                  <th>Lifecycle</th>
                  <th className="num">Recipients</th>
                  <th className="num">Arm A</th>
                  <th className="num">Arm B</th>
                </tr>
              </thead>
              <tbody>
                {preview.lifecycles.map((l) => (
                  <tr key={l.lifecycle}>
                    <td className="table-card-title">{LIFECYCLE_LABEL[l.lifecycle]}</td>
                    <td data-label="Recipients" className="num">
                      {fmtCount(l.recipients)}
                    </td>
                    <td data-label="Arm A" className="num">
                      {fmtCount(l.variants.A)}
                    </td>
                    <td data-label="Arm B" className="num">
                      {fmtCount(l.variants.B)}
                    </td>
                  </tr>
                ))}
                <tr className="campaign-row-total">
                  <td className="table-card-title">All lifecycles</td>
                  <td data-label="Recipients" className="num">
                    {fmtCount(preview.total_recipients)}
                  </td>
                  <td data-label="Arm A" className="num">
                    {fmtCount(totals?.A ?? null)}
                  </td>
                  <td data-label="Arm B" className="num">
                    {fmtCount(totals?.B ?? null)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {preview.over_cap && (
            <div className="tag tag-error campaigns-notice">
              {fmtCount(preview.total_recipients)} recipients is over the {fmtCount(preview.max_recipients)} one
              edition may go to.
            </div>
          )}
          {goalRows.length > 0 && (
            <>
              <h3>By goal</h3>
              <div className="table-scroll-x">
                <table className="campaign-recipients table-cards">
                  <thead>
                    <tr>
                      <th>Lifecycle · goal</th>
                      <th className="num">Recipients</th>
                      <th>Body</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goalRows.map(({ lifecycle, goal: g }) => (
                      <tr key={`${lifecycle}:${g.goal}`}>
                        <td className="table-card-title">
                          {LIFECYCLE_LABEL[lifecycle]} · {GOAL_LABEL[g.goal]}
                        </td>
                        <td data-label="Recipients" className="num">
                          {fmtCount(g.recipients)}
                        </td>
                        <td data-label="Body">{bodyText(g.goal, lifecycle, g.body_key)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <p className="member-note subtle">
            As of {fmtWhen(preview.as_of)}.
            {preview.outside_lifecycles > 0 &&
              ` ${countOf(preview.outside_lifecycles, "active member")} in lifecycles this edition does not go to.`}
            {preview.without_body > 0 &&
              ` ${countOf(preview.without_body, "member")} with no body for their goal, left out.`}
          </p>
          <LeftOut title="Left out" counts={preview.excluded} channel="email" />
        </>
      )}
    </>
  );
}

function ComparisonCells({
  c,
  missing,
  label,
}: {
  readonly c: ExperimentComparison | null;
  readonly missing: "went_to_no_one" | "not_captured" | null;
  readonly label: string;
}): JSX.Element {
  // An arm that went to no one is final ("—", as the grid shows it); a
  // checkpoint still to come is pending ("…").
  const none = missing === "went_to_no_one" ? "—" : "…";
  return (
    <>
      <td data-label={`A · ${label}`} className="num">
        {c === null ? none : fmtPct(c.a.rate, 2)}
      </td>
      <td data-label={`B · ${label}`} className="num">
        {c === null ? none : fmtPct(c.b.rate, 2)}
      </td>
      <td data-label="Chance B is better" className="num">
        {c === null
          ? missing === "went_to_no_one"
            ? "an arm went to no one"
            : "not captured yet"
          : fmtChance(c.chanceBBetter)}
      </td>
    </>
  );
}

function ResultsSection({
  edition,
  campaigns,
  now,
}: {
  readonly edition: NewsletterEdition;
  readonly campaigns: ReadonlyArray<CampaignSummary>;
  readonly now: Date;
}): JSX.Element {
  const e = edition;
  const [checkpoint, setCheckpoint] = useState<EmailCampaignCheckpointKey>(e.decision_checkpoint);
  const grid = useMemo(() => editionGrid(e, campaigns, checkpoint, now), [e, campaigns, checkpoint, now]);
  const readout = useMemo(() => experimentReadout(e, campaigns, checkpoint), [e, campaigns, checkpoint]);
  const metricLabel = PRIMARY_METRIC_LABEL[e.primary_metric];
  const pooled = readout.pooled;
  const note = poolNote(readout);

  return (
    <>
      <h3>Results</h3>
      <div className="campaigns-controls">
        <label>
          As of{" "}
          <select
            value={checkpoint}
            onChange={(ev) => {
              const next = EMAIL_CAMPAIGN_CHECKPOINTS.find((k) => k.key === ev.target.value);
              if (next !== undefined) setCheckpoint(next.key);
            }}
          >
            {EMAIL_CAMPAIGN_CHECKPOINTS.map(({ key }) => (
              <option key={key} value={key}>
                {key}
                {key === e.decision_checkpoint ? " (decides the test)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="table-scroll-x">
        <table className="campaign-grid table-cards">
          <thead>
            <tr>
              <th>Lifecycle · arm</th>
              <th className="num">Sent</th>
              <th className="num">Delivered</th>
              <th className="num">Unique opens</th>
              <th className="num">Unique clicks</th>
              <th className="num">Unsubscribes</th>
              <th className="num">Bounces</th>
            </tr>
          </thead>
          <tbody>
            {grid.map((r) => {
              const m = r.metrics;
              const rates = campaignRates(m);
              const waiting = m === null ? cellView(r.cell, "Unique clicks").title : undefined;
              return (
                <tr key={`${r.lifecycle}:${r.variant}`} title={waiting}>
                  <td className="table-card-title">
                    {LIFECYCLE_LABEL[r.lifecycle]} · {r.variant}
                    <div className="subtle">{r.subject}</div>
                  </td>
                  <td data-label="Sent" className="num">
                    {fmtCount(metricCount(m, "sent"))}
                  </td>
                  <td data-label="Delivered" className="num">
                    {fmtCount(metricCount(m, "delivered"))}
                  </td>
                  <td data-label="Unique opens" className="num">
                    {fmtCount(metricCount(m, "unique_opened"))}
                    {rates.openRate !== null && <span className="subtle campaign-rate">{fmtPct(rates.openRate)}</span>}
                  </td>
                  <td data-label="Unique clicks" className="num">
                    {fmtCount(metricCount(m, "unique_clicked"))}
                    {rates.clickRate !== null && <span className="subtle campaign-rate">{fmtPct(rates.clickRate, 2)}</span>}
                  </td>
                  <td data-label="Unsubscribes" className="num">
                    {fmtCount(metricCount(m, "unsubscribed"))}
                    {rates.unsubscribeRate !== null && (
                      <span className="subtle campaign-rate">{fmtPct(rates.unsubscribeRate, 2)}</span>
                    )}
                  </td>
                  <td data-label="Bounces" className="num">
                    {fmtCount(metricCount(m, "bounced"))}
                    {rates.bounceRate !== null && <span className="subtle campaign-rate">{fmtPct(rates.bounceRate, 2)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3>
        Arm B against arm A · {metricLabel} at {checkpoint}
      </h3>
      {pooled.verdict === "no_data" ? (
        <p className="empty">No numbers at {checkpoint} yet.</p>
      ) : (
        <>
          <p className="campaign-verdict">
            <strong>{fmtChance(pooled.chanceBBetter)} chance B is better.</strong> {verdictText(pooled)}{" "}
            A: {fmtPct(pooled.a.rate, 2)} of {fmtCount(pooled.a.delivered)} delivered · B:{" "}
            {fmtPct(pooled.b.rate, 2)} of {fmtCount(pooled.b.delivered)} delivered.
            {note !== null && ` ${note}`}
          </p>
          <div className="table-scroll-x">
            <table className="campaign-experiment table-cards">
              <thead>
                <tr>
                  <th>Lifecycle</th>
                  <th className="num">A · {metricLabel}</th>
                  <th className="num">B · {metricLabel}</th>
                  <th className="num">Chance B is better</th>
                </tr>
              </thead>
              <tbody>
                {readout.byLifecycle.map((l) => (
                  <tr key={l.lifecycle}>
                    <td className="table-card-title">{LIFECYCLE_LABEL[l.lifecycle]}</td>
                    <ComparisonCells c={l.comparison} missing={l.missing} label={metricLabel} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="member-note subtle">
        The chance is worked out from the two arms&apos; counts (a Bayesian comparison starting from no
        opinion); a winner is named at {String(Math.round(EXPERIMENT_WIN_CHANCE * 100))}% either way.
        {e.primary_metric === "unsubscribe_rate" ? " For unsubscribes, fewer is better." : ""}
      </p>
    </>
  );
}

/**
 * An admin's tag picker for one edition (email-newsletter-tag-audience,
 * 2026-10-31): the tags as chips with a searchable list to add one, as on
 * `/newsletter`. Saving makes the next version with only the tags changed
 * (`set_newsletter_edition_audience`), which clears any approval. A refusal
 * goes to the panel (`onError`): it usually means the edition moved on, and
 * the re-read remounts this editor or takes it away.
 */
function EditionTagsEditor({
  edition,
  onChanged,
  onError,
}: {
  readonly edition: NewsletterEdition;
  readonly onChanged: () => void;
  readonly onError: (message: string | null) => void;
}): JSX.Element {
  const e = edition;
  const current = useMemo(() => e.audience_tags.map((t) => t.slug), [e.audience_tags]);
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<ReadonlyArray<string>>(current);
  const [saving, setSaving] = useState(false);
  // Every tag, A–Z, with its active members — loaded once the picker opens.
  const tagList = useCachedResource("lookup:tags", () => api.lookupOptions("tags"), { enabled: editing });
  const options = useMemo((): ReadonlyArray<SearchableSelectOption> | null => {
    if (tagList.data === undefined) return null;
    return tagList.data.options.map((o) => ({
      value: o.value,
      label: o.label,
      meta: fmtCount(o.member_count ?? 0),
      keywords: o.value,
    }));
  }, [tagList.data]);
  const unchanged = sameTags(picked, current);

  async function save(): Promise<void> {
    const next = e.draft_version + 1;
    const who =
      picked.length === 0
        ? "everyone in its lifecycles again"
        : `only members carrying ${picked.length === 1 ? "that tag" : "any of those tags"}`;
    const ok = window.confirm(
      `Save version ${String(next)} of “${e.title}”, going to ${who}?${e.approval !== null ? " This clears its approval: approve the new version before it can be sent." : ""}`,
    );
    if (!ok) return;
    setSaving(true);
    onError(null);
    try {
      const res = await dispatchAction<
        { edition_id: string; version: number; audience_tag_slugs: ReadonlyArray<string> },
        { saved: boolean }
      >("set_newsletter_edition_audience", {
        edition_id: e.edition_id,
        version: e.draft_version,
        audience_tag_slugs: picked,
      });
      if (res.ok) setEditing(false);
      else onError(res.message);
    } catch {
      onError("Could not reach the server, so the tags may or may not be saved. Reload to see.");
    } finally {
      setSaving(false);
    }
    // Re-read whatever the answer: a refusal (a newer version saved, the
    // edition locked) means the panel shows an edition that has moved on.
    onChanged();
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="link-button"
        onClick={() => {
          setPicked(current);
          onError(null);
          setEditing(true);
        }}
      >
        {current.length === 0 ? "Pick tags" : "Change tags"}
      </button>
    );
  }
  return (
    <div className="campaign-tags-editor">
      <AudienceListPicker
        picked={picked}
        options={options}
        onChange={setPicked}
        max={NEWSLETTER_AUDIENCE_TAGS_MAX}
        noun="tag"
        emptyText="This studio has no tags yet."
        loadError={
          tagList.error !== null && tagList.data === undefined
            ? `The tags could not be loaded: ${tagList.error.message}`
            : null
        }
        note="Only members carrying ANY of these tags get it; with none, everyone in its lifecycles does. Consent rules still apply."
      />
      <div className="campaign-approve">
        <button type="button" onClick={() => void save()} disabled={saving || unchanged}>
          {saving ? "Saving…" : `Save as v${String(e.draft_version + 1)}`}
        </button>
        <button type="button" className="link-button" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
        <span className="subtle">A new version clears the approval.</span>
      </div>
    </div>
  );
}

/** What `send_email_newsletter` answers (202, queued). */
interface EmailNewsletterQueued {
  readonly run_id: string;
  readonly scheduled_for: string;
  readonly recipient_count: number;
  readonly is_test: boolean;
}

/** "1 member", "3 members", "1 member tagged testing". */
function queuedAudience(q: EmailNewsletterQueued): string {
  return `${fmtCount(q.recipient_count)} ${q.recipient_count === 1 ? "member" : "members"}${q.is_test ? " tagged testing" : ""}`;
}

/**
 * Send an approved edition from the page (email-newsletter-tag-audience,
 * 2026-10-31; Mario: "the full WhatsApp match"). A send cannot be recalled
 * once it goes, so it is built like `/newsletter`'s: nothing sends until the
 * total the live preview shows is typed back, and the send confirms each
 * lifecycle's count from that preview — the action re-plans and refuses if
 * any moved. Send now, or Schedule for a time within 14 days, in the
 * studio's time. One request id per preview: a double press replays. A
 * refusal's message is the panel's to show: the fresh preview it forces
 * unmounts this one.
 */
function SendPanel({
  edition,
  preview,
  onSending,
  onQueued,
  onRefused,
}: {
  readonly edition: NewsletterEdition;
  readonly preview: NewsletterEditionPreview;
  /** A send is starting: an earlier refusal no longer applies. */
  readonly onSending: () => void;
  readonly onQueued: (queued: EmailNewsletterQueued) => void;
  /** A refusal, with what to show: the preview must be taken again before anything can be sent. */
  readonly onRefused: (message: string) => void;
}): JSX.Element {
  const e = edition;
  const [when, setWhen] = useState<"now" | "schedule">("now");
  const [at, setAt] = useState("");
  const [typed, setTyped] = useState("");
  const [sending, setSending] = useState(false);
  const requestId = useMemo(() => generateRequestIdV7(), [preview]);
  const tz = studioTimeZone();
  const window_ = scheduleWindow(new Date());
  const atIso = when === "schedule" ? zonedDatetimeInputToIso(at, tz) : "";
  const scheduleOk = when === "now" || (atIso !== "" && scheduleInWindow(new Date(atIso), new Date()));
  const blocked = sendBlockedReason(preview);
  const total = preview.total_recipients;
  const people = `${fmtCount(total)} ${e.is_test ? "tagged testing" : total === 1 ? "member" : "members"}`;
  const canSend = blocked === null && typedTotalMatches(typed, preview) && scheduleOk && !sending;

  async function send(): Promise<void> {
    setSending(true);
    onSending();
    let refusal: string;
    try {
      const res = await dispatchAction<
        {
          edition_id: string;
          version: number;
          confirm_counts: Partial<Record<MarketingLifecycle, number>>;
          scheduled_for?: string;
        },
        EmailNewsletterQueued
      >(
        "send_email_newsletter",
        {
          edition_id: e.edition_id,
          version: e.draft_version,
          confirm_counts: confirmCountsFromPreview(preview),
          ...(when === "schedule" ? { scheduled_for: atIso } : {}),
        },
        { requestId },
      );
      if (res.ok) {
        onQueued(res.response);
        return;
      }
      refusal = res.message;
    } catch {
      refusal = "Could not reach the server, so it may or may not have been scheduled. Reload to see before trying again.";
    } finally {
      setSending(false);
      setTyped("");
    }
    // The audience moved, a newer version was saved, or it was sent from
    // elsewhere: the only way on is a fresh preview, so a stale count cannot
    // be sent.
    onRefused(refusal);
  }

  return (
    <div className="campaign-send">
      <h3>{e.is_test ? "Send this test edition" : "Send"}</h3>
      {e.is_test && (
        <p className="member-note">
          A test edition goes only to members tagged testing — {fmtCount(total)} right now. No one else gets it.
        </p>
      )}
      {blocked !== null ? (
        <div className="tag tag-warn campaigns-notice">{blocked}</div>
      ) : (
        <>
          <div className="campaign-send-when" role="radiogroup" aria-label="When to send">
            <label>
              <input type="radio" name={`send-when-${e.edition_id}`} checked={when === "now"} onChange={() => setWhen("now")} disabled={sending} />{" "}
              Send now
            </label>
            <label>
              <input
                type="radio"
                name={`send-when-${e.edition_id}`}
                checked={when === "schedule"}
                onChange={() => setWhen("schedule")}
                disabled={sending}
              />{" "}
              Schedule for
            </label>
            {when === "schedule" && (
              <>
                <input
                  type="datetime-local"
                  aria-label="Send at"
                  value={at}
                  min={isoToZonedDatetimeInput(window_.min.toISOString(), tz)}
                  max={isoToZonedDatetimeInput(window_.max.toISOString(), tz)}
                  onChange={(ev) => setAt(ev.target.value)}
                  disabled={sending}
                />
                <span className="subtle">
                  {tz} time, up to 14 days ahead.
                  {at !== "" && !scheduleOk && " That time is past or more than 14 days away."}
                </span>
              </>
            )}
          </div>
          <div className="campaign-send-confirm">
            <label htmlFor={`send-confirm-${e.edition_id}`}>
              Type <strong>{fmtCount(total)}</strong> to {when === "now" ? "send it" : "schedule it"}
            </label>
            <input
              id={`send-confirm-${e.edition_id}`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={typed}
              onChange={(ev) => setTyped(ev.target.value)}
              disabled={sending}
            />
            <button type="button" className="campaign-send-button" onClick={() => void send()} disabled={!canSend}>
              {sending ? "Sending…" : when === "now" ? `Send to ${people}` : `Schedule for ${people}`}
            </button>
          </div>
          <p className="member-note subtle">
            Once it goes it cannot be recalled. Until its time, Withdraw takes a scheduled send back; while it
            sends, Stop sending holds the batches not yet gone.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Withdraw edition (`withdraw_newsletter_edition`), for an admin: an edition
 * never scheduled — awaiting approval, or approved — cancelled for good, after
 * a confirm that says so. The panel re-reads whatever the answer, so a refusal
 * (scheduled by someone else meanwhile) shows the edition as it now is; the
 * message is the panel's, so it stays when this button goes.
 */
function WithdrawEditionControl({
  edition,
  onChanged,
  onError,
}: {
  readonly edition: NewsletterEdition;
  readonly onChanged: () => void;
  readonly onError: (message: string | null) => void;
}): JSX.Element {
  const e = edition;
  const [busy, setBusy] = useState(false);

  async function withdraw(): Promise<void> {
    const ok = window.confirm(
      `Withdraw ${e.is_test ? "the test edition " : ""}“${e.title}” for good? It will never be sent. This cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    onError(null);
    try {
      const res = await dispatchAction<{ edition_id: string }, { from_status: string }>("withdraw_newsletter_edition", {
        edition_id: e.edition_id,
      });
      if (!res.ok) onError(res.message);
    } catch {
      onError("Could not reach the server, so it may or may not be withdrawn. Reload to see.");
    } finally {
      setBusy(false);
    }
    onChanged();
  }

  return (
    <div className="campaign-approve">
      <button type="button" className="campaign-stop-button" onClick={() => void withdraw()} disabled={busy}>
        {busy ? "Withdrawing…" : "Withdraw edition"}
      </button>
      <span className="subtle">Cancels it for good: it will never be sent.</span>
    </div>
  );
}

/**
 * The emergency stop beside a send (`cancel_newsletter_send`), for an admin:
 * Withdraw while it waits for its time — its rows go and it stays approved —
 * and Stop sending while it sends — the next batch does not go.
 */
function CancelSendControl({
  edition,
  kind,
  onChanged,
}: {
  readonly edition: NewsletterEdition;
  readonly kind: "withdraw" | "stop";
  readonly onChanged: () => void;
}): JSX.Element {
  const e = edition;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cancel(): Promise<void> {
    const ok = window.confirm(
      kind === "withdraw"
        ? `Withdraw the scheduled send of “${e.title}”? Nothing has gone out yet. It stays approved, ready to send at another time.`
        : `Stop sending “${e.title}”? Emails already handed to Resend cannot be called back; everyone not yet sent is skipped, and the edition is cancelled.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await dispatchAction<{ edition_id: string }, { state: string }>("cancel_newsletter_send", {
        edition_id: e.edition_id,
      });
      if (!res.ok) setError(res.message);
    } catch {
      setError("Could not reach the server, so it may or may not have stopped. Reload to see.");
    } finally {
      setBusy(false);
    }
    onChanged();
  }

  return (
    <div className="campaign-approve">
      <button type="button" className="campaign-stop-button" onClick={() => void cancel()} disabled={busy}>
        {busy ? "Stopping…" : kind === "withdraw" ? "Withdraw" : "Stop sending"}
      </button>
      <span className="subtle">
        {kind === "withdraw"
          ? "Takes the scheduled send back; it stays approved."
          : "The batches not yet gone are held; the edition is cancelled."}
      </span>
      {error !== null && <div className="tag tag-error campaigns-notice">{error}</div>}
    </div>
  );
}

function EditionPanel({
  edition,
  canApprove,
  now,
  onChanged,
  onClose,
}: {
  readonly edition: NewsletterEdition;
  readonly canApprove: boolean;
  readonly now: Date;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const e = edition;
  const status = editionStatusView(e);
  const countable = e.status === "awaiting_approval" || e.status === "approved";
  const hasRows = e.campaigns.length > 0;
  const panelRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const [preview, setPreview] = useState<NewsletterEditionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReadonlyArray<CampaignSummary> | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [detail, setDetail] = useState<NewsletterEditionDetail | null>(null);
  const [viewKey, setViewKey] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  /** Bumped to take the preview again — after a refused send, a stale count is never sent. */
  const [previewReloads, setPreviewReloads] = useState(0);
  /** The send this panel just queued; kept while the list re-reads to `scheduled`. */
  const [queued, setQueued] = useState<EmailNewsletterQueued | null>(null);
  /** A refused send or tag save: kept here, since the re-read that follows remounts or removes the control. */
  const [sendError, setSendError] = useState<string | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const cancel = cancelKind(e, canApprove);

  // The "queued" notice lasts while the send it names is pending
  // (`queuedNoticeEnds`): after a withdraw, a stop or the end of the send it
  // would contradict the status.
  const lastStatus = useRef(e.status);
  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = e.status;
    if (queuedNoticeEnds(was, e.status)) setQueued(null);
  }, [e.status]);

  // Opening an edition brings its panel into view: below a long list (on a
  // phone, below the fold) a tap would otherwise look dead (2026-10-18
  // review). The panel is keyed by edition, so this runs once per opening.
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!countable) return;
    let cancelled = false;
    void dispatchAction<{ edition_id: string }, NewsletterEditionPreview>("preview_email_newsletter", {
      edition_id: e.edition_id,
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setPreview(res.response);
        else setPreviewError(res.message);
      })
      .catch(() => {
        if (!cancelled) setPreviewError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [e.edition_id, e.draft_version, countable, previewReloads]);

  // The edition's own rows, read when the panel opens and again when its
  // status moves (a send starting or finishing) — never out of the whole
  // newest-first list, where later sends push an old edition past the limit
  // (2026-10-18 review).
  useEffect(() => {
    setRows(null);
    setRowsError(null);
    if (!hasRows) return;
    let cancelled = false;
    void dispatchAction<CampaignsListInput, CampaignsList>("list_email_campaigns", {
      edition_id: e.edition_id,
      // Tests included: a test edition is how a send is proven before it
      // reaches members.
      include_tests: true,
      include_drafts: false,
      limit: EDITION_ROWS_LIMIT,
      learnings_limit: 0,
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setRows(res.response.campaigns);
        else setRowsError(res.message);
      })
      .catch(() => {
        if (!cancelled) setRowsError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [e.edition_id, e.status, hasRows]);

  // A body as a member sees it, so an approver reads what they approve. The
  // full edition is read once per version shown; one saved since is shown as
  // what it is, and the list re-read so the panel names that version too.
  async function viewBody(key: string): Promise<void> {
    setViewError(null);
    if (detail !== null && detail.draft_version === e.draft_version) {
      if (detail.bodies?.[key] === undefined) setViewError(`Version ${String(detail.draft_version)} has no ${bodyKeyLabel(key)} body.`);
      else setViewKey(key);
      return;
    }
    setViewLoading(true);
    try {
      const res = await dispatchAction<{ edition_id: string }, { edition: NewsletterEditionDetail }>(
        "get_newsletter_edition",
        { edition_id: e.edition_id },
      );
      if (!res.ok) {
        setViewError(res.message);
        return;
      }
      const full = res.response.edition;
      setDetail(full);
      if (full.draft_version !== e.draft_version) onChanged();
      if (full.bodies?.[key] === undefined) {
        setViewError(`Version ${String(full.draft_version)} has no ${bodyKeyLabel(key)} body.`);
      } else {
        setViewKey(key);
      }
    } catch {
      setViewError(UNREACHABLE);
    } finally {
      setViewLoading(false);
    }
  }

  async function approve(): Promise<void> {
    const reach =
      preview === null
        ? e.is_test
          ? " A test edition goes only to members tagged testing."
          : ""
        : ` Right now it would go to ${fmtCount(preview.total_recipients)} ${e.is_test ? "members tagged testing" : "members"}${e.audience_tags.length > 0 ? ` (${audienceText(e)})` : ""}.`;
    const ok = window.confirm(
      `Approve version ${String(e.draft_version)} of ${e.is_test ? "the test edition " : ""}“${e.title}”?${reach} Saving a new version later clears this approval.`,
    );
    if (!ok) return;
    setApproving(true);
    setApproveError(null);
    try {
      const res = await dispatchAction<{ edition_id: string; version: number }, { approved: boolean }>(
        "approve_newsletter_edition",
        { edition_id: e.edition_id, version: e.draft_version },
      );
      if (!res.ok) setApproveError(res.message);
    } catch {
      setApproveError("Could not reach the server, so it may or may not be approved. Reload to see.");
    } finally {
      setApproving(false);
    }
    // Re-read whatever the answer: a refusal (a newer version saved, the
    // edition locked) means this panel shows an edition that has moved on,
    // and every retry would fail the same way (2026-10-18 review). The panel
    // is keyed by edition, so the message stays.
    onChanged();
  }

  const viewHtml = viewKey === null || detail === null ? undefined : detail.bodies?.[viewKey];

  return (
    <section ref={panelRef} className="panel campaign-detail" aria-label={`Edition: ${e.title}`}>
      <div className="campaign-detail-head">
        <h2 ref={headingRef} tabIndex={-1}>
          {e.title}
        </h2>
        <div className="campaign-detail-actions">
          <button type="button" className="link-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <dl className="campaign-detail-facts">
        <dt>Status</dt>
        <dd>
          <span className={TONE_CLASS[status.tone]}>{status.text}</span>
          {e.is_test ? " Test edition: members tagged testing only." : ""} {sendText(e)}
        </dd>
        <dt>Approval</dt>
        <dd>
          {approvalText(e.approval) ?? "Not approved yet"}
          {e.approval !== null && ` · ${fmtWhen(e.approval.at)}`}
        </dd>
        <dt>Goes to</dt>
        <dd>
          {audienceText(e)}
          {canPickTags(e, canApprove) && (
            <div>
              <EditionTagsEditor
                key={`${e.edition_id}:${String(e.draft_version)}`}
                edition={e}
                onChanged={onChanged}
                onError={setTagsError}
              />
            </div>
          )}
          {tagsError !== null && <div className="tag tag-error campaigns-notice">{tagsError}</div>}
        </dd>
        <dt>From</dt>
        <dd>{e.from_address}</dd>
        <dt>Arm A</dt>
        <dd>
          {e.subject_a}
          <span className="subtle campaign-rate">{e.preview_text_a}</span>
        </dd>
        <dt>Arm B</dt>
        <dd>
          {e.subject_b}
          <span className="subtle campaign-rate">{e.preview_text_b}</span>
        </dd>
        <dt>Testing</dt>
        <dd>{e.experiment_variable}</dd>
        <dt>Hypothesis</dt>
        <dd>{e.hypothesis}</dd>
        <dt>Decided on</dt>
        <dd>
          {PRIMARY_METRIC_LABEL[e.primary_metric]} at {e.decision_checkpoint}
        </dd>
        {e.topic !== null && (
          <>
            <dt>Topic</dt>
            <dd>{e.topic}</dd>
          </>
        )}
        {e.hook !== null && (
          <>
            <dt>Hook</dt>
            <dd>{e.hook}</dd>
          </>
        )}
        <dt>Bodies</dt>
        <dd>
          <span className="campaign-bodies">
            {e.body_keys.map((k) => (
              <button
                key={k}
                type="button"
                className="link-button"
                disabled={viewLoading}
                onClick={() => void viewBody(k)}
              >
                {bodyKeyLabel(k)}
              </button>
            ))}
          </span>
          <span className="subtle">{viewLoading ? "Opening…" : "Open one to read it as a member will."}</span>
          {viewError !== null && <div className="tag tag-error campaigns-notice">{viewError}</div>}
        </dd>
        <dt>Sources</dt>
        <dd>
          {countOf(e.claims_count, "claim")}, {countOf(e.research_count, "research note")}
        </dd>
        <dt>Saved</dt>
        <dd>
          {fmtWhen(e.updated_at)} · {e.slug}
        </dd>
      </dl>

      {showApproveButton(e, canApprove) && (
        <div className="campaign-approve">
          <button type="button" onClick={() => void approve()} disabled={approving}>
            {approving ? "Approving…" : `Approve v${String(e.draft_version)}`}
          </button>
          <span className="subtle">Approves exactly this version; saving a new one clears it.</span>
        </div>
      )}
      {approveError !== null && <div className="tag tag-error campaigns-notice">{approveError}</div>}

      {showSendPanel(e, canApprove) && preview !== null && preview.draft_version === e.draft_version && (
        <SendPanel
          edition={e}
          preview={preview}
          onSending={() => setSendError(null)}
          onQueued={(q) => {
            setQueued(q);
            onChanged();
          }}
          onRefused={(message) => {
            setSendError(message);
            setPreviewReloads((n) => n + 1);
            onChanged();
          }}
        />
      )}
      {sendError !== null && <div className="tag tag-error campaigns-notice">{sendError}</div>}
      {queued !== null && (
        <div className="tag tag-success campaigns-notice">
          {new Date(queued.scheduled_for).getTime() <= Date.now() + 60_000
            ? `Sending to ${queuedAudience(queued)} now.`
            : `Scheduled for ${fmtWhen(queued.scheduled_for)}: ${queuedAudience(queued)}.`}{" "}
          The results below fill in as it goes.
        </div>
      )}
      {cancel !== null && <CancelSendControl edition={e} kind={cancel} onChanged={onChanged} />}
      {showWithdrawEdition(e, canApprove) && (
        <WithdrawEditionControl edition={e} onChanged={onChanged} onError={setWithdrawError} />
      )}
      {withdrawError !== null && <div className="tag tag-error campaigns-notice">{withdrawError}</div>}

      {countable && <RecipientsSection preview={preview} error={previewError} isTest={e.is_test} />}
      {hasRows ? (
        rowsError !== null ? (
          <>
            <h3>Results</h3>
            <div className="tag tag-error campaigns-notice">Results unavailable: {rowsError}</div>
          </>
        ) : rows === null ? (
          <p className="empty">Loading the results…</p>
        ) : (
          <ResultsSection edition={e} campaigns={rows} now={now} />
        )
      ) : (
        !countable && (
          <p className="member-note subtle">
            {e.status === "cancelled" ? "Withdrawn before it went out." : "No send rows yet."}
          </p>
        )
      )}

      {viewKey !== null && detail !== null && viewHtml !== undefined && (
        <EmailPreviewModal
          title={`${detail.title} · v${String(detail.draft_version)} · ${bodyKeyLabel(viewKey)}`}
          subtitle={`As a member with no first name on file sees it, with arm A's preview text. Arm B goes out as “${detail.subject_b}”. Its unsubscribe link goes nowhere.`}
          subject={detail.subject_a}
          html={sampleNewsletterBody(viewHtml, detail.preview_text_a)}
          onClose={() => setViewKey(null)}
        />
      )}
    </section>
  );
}

function EditionsTab(): JSX.Element {
  const [list, setList] = useState<NewsletterEditionsList | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const now = useMemo(() => new Date(), [list]);

  useEffect(() => {
    let cancelled = false;
    void dispatchAction<{ limit: number }, NewsletterEditionsList>("list_newsletter_editions", {
      limit: EDITIONS_LIMIT,
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setList(res.response);
          setListError(null);
        } else {
          setListError(res.message);
        }
      })
      .catch(() => {
        if (!cancelled) setListError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [reloads]);

  // Re-read while an edition goes out (`editionsPollDelay`), and only while
  // the browser tab is visible: every read is an audited dispatch. A wait
  // that runs out while the tab is hidden re-reads when it is shown again.
  useEffect(() => {
    if (list === null) return;
    const delay = editionsPollDelay(list.editions, new Date());
    if (delay === null) return;
    let due = false;
    const reload = (): void => setReloads((n) => n + 1);
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") reload();
      else due = true;
    }, delay);
    const onVisibility = (): void => {
      if (due && document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [list, reloads]);

  const editions = list?.editions ?? [];
  const selected = editions.find((e) => e.edition_id === selectedId) ?? null;

  return (
    <>
      <p className="member-note">
        Each edition of the newsletter: its version, who approved it and how, and its A/B test. An
        edition goes out only once its current version is approved; a new version clears the
        approval. Open one to read each of its bodies as a member will, and once it is sent, its
        numbers for each lifecycle and arm, and how arm B did against arm A.
      </p>
      {listError !== null && <div className="tag tag-error campaigns-notice">{listError}</div>}
      <section className="panel">
        <h2>Editions</h2>
        {list === null && listError === null && <p className="empty">Loading…</p>}
        {list !== null && editions.length === 0 && (
          <p className="empty">No edition yet. The newsletter lane saves each one as it drafts it.</p>
        )}
        {editions.length > 0 && (
          <div className="table-scroll-x">
            <table className="campaign-editions table-cards">
              <thead>
                <tr>
                  <th>Edition</th>
                  <th>Status</th>
                  <th>Approval</th>
                  <th>Goes to</th>
                  <th>Send</th>
                </tr>
              </thead>
              <tbody>
                {editions.map((e) => {
                  const status = editionStatusView(e);
                  return (
                    <tr key={e.edition_id} className={e.edition_id === selectedId ? "is-selected" : undefined}>
                      <td className="table-card-title">
                        <button
                          type="button"
                          className="campaign-open"
                          aria-expanded={e.edition_id === selectedId}
                          onClick={() => setSelectedId(e.edition_id === selectedId ? null : e.edition_id)}
                        >
                          {e.title}
                        </button>{" "}
                        {e.is_test && <span className="tag">Test</span>}
                        <div className="subtle">{e.slug}</div>
                      </td>
                      <td data-label="Status">
                        <span className={TONE_CLASS[status.tone]}>{status.text}</span>
                      </td>
                      <td data-label="Approval">
                        {approvalText(e.approval) ?? "—"}
                        {e.approval !== null && <span className="subtle campaign-rate">{fmtWhen(e.approval.at)}</span>}
                      </td>
                      <td data-label="Goes to">{audienceText(e)}</td>
                      <td data-label="Send">{sendText(e)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {selected !== null && (
        <EditionPanel
          key={selected.edition_id}
          edition={selected}
          canApprove={list?.can_approve_on_dashboard === true}
          now={now}
          onChanged={() => setReloads((n) => n + 1)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Audiences tab.
// ---------------------------------------------------------------------------

function AudienceCount({
  members,
  email,
  whatsapp,
}: {
  readonly members: number;
  readonly email: number;
  readonly whatsapp: number;
}): JSX.Element {
  return (
    <>
      {fmtCount(members)}
      <span className="subtle campaign-rate">
        (email {fmtCount(email)}, WhatsApp {fmtCount(whatsapp)})
      </span>
    </>
  );
}

function AudiencesTab(): JSX.Element {
  const [preview, setPreview] = useState<MarketingAudiencePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void dispatchAction<Record<string, never>, MarketingAudiencePreview>("preview_marketing_audience", {})
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setPreview(res.response);
        else setError(res.message);
      })
      .catch(() => {
        if (!cancelled) setError(UNREACHABLE);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matrix = useMemo(() => (preview === null ? null : audienceMatrix(preview)), [preview]);

  return (
    <>
      <p className="member-note">
        Every active member by lifecycle and goal, and how many each channel can reach.
        Lifecycles come from the last {String(preview?.lapse_days ?? 60)} days: members (a membership,
        a live package or regular sessions), trial (an intro offer or a first session), win-back
        (paid or trained before, nothing since) and leads (everyone else).
      </p>
      {error !== null && <div className="tag tag-error campaigns-notice">{error}</div>}
      <section className="panel">
        <h2>Who marketing can reach</h2>
        {preview === null && error === null && <p className="empty">Counting…</p>}
        {preview !== null && matrix !== null && (
          <>
            {goalDataThin(preview) && (
              <div className="campaigns-hint">
                Most members have no goal on file yet, so they count as General. Uploading their
                consultation forms (on each member&apos;s page) grows the goal columns.
              </div>
            )}
            <div className="table-scroll-x">
              <table className="campaign-audiences table-cards">
                <thead>
                  <tr>
                    <th>Lifecycle</th>
                    {MARKETING_GOALS.map((g) => (
                      <th key={g} className="num">
                        {GOAL_LABEL[g]}
                      </th>
                    ))}
                    <th className="num">All goals</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((r) => (
                    <tr key={r.lifecycle}>
                      <td className="table-card-title">{LIFECYCLE_LABEL[r.lifecycle]}</td>
                      {r.cells.map((c) => (
                        <td key={c.goal} data-label={GOAL_LABEL[c.goal]} className="num">
                          <AudienceCount members={c.members} email={c.email_reachable} whatsapp={c.whatsapp_reachable} />
                        </td>
                      ))}
                      <td data-label="All goals" className="num">
                        <AudienceCount members={r.total.members} email={r.total.email} whatsapp={r.total.whatsapp} />
                      </td>
                    </tr>
                  ))}
                  <tr className="campaign-row-total">
                    <td className="table-card-title">All lifecycles</td>
                    {matrix.goalTotals.map((g) => (
                      <td key={g.goal} data-label={GOAL_LABEL[g.goal]} className="num">
                        <AudienceCount members={g.members} email={g.email} whatsapp={g.whatsapp} />
                      </td>
                    ))}
                    <td data-label="All goals" className="num">
                      <AudienceCount
                        members={preview.totals.members}
                        email={preview.totals.email_reachable}
                        whatsapp={preview.totals.whatsapp_reachable}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="member-note subtle">
              Each cell: active members, and in brackets how many the email newsletter and a WhatsApp
              marketing send can reach. As of {fmtWhen(preview.as_of)}.
            </p>
            <div className="campaign-leftout-grid">
              <LeftOut
                title={`Email newsletter: ${fmtCount(preview.totals.email_reachable)} of ${fmtCount(preview.totals.members)}`}
                counts={preview.excluded.email}
                channel="email"
              />
              <LeftOut
                title={`WhatsApp: ${fmtCount(preview.totals.whatsapp_reachable)} of ${fmtCount(preview.totals.members)}`}
                counts={preview.excluded.whatsapp}
                channel="whatsapp"
              />
            </div>
            <p className="member-note subtle">
              The monthly WhatsApp newsletter still counts by its own rules, so its numbers can differ
              slightly until it moves onto these.
            </p>
          </>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// The page.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Subscribers tab (newsletter-subscribers, 2026-10-24).
// ---------------------------------------------------------------------------

function fmtDate(iso: string | null): string {
  return iso === null ? "—" : fmtDay(iso);
}

/** The "Newsletters" cell's hover: the last one, and the next one waiting. */
function newslettersTitle(s: NewsletterSubscriber): string | undefined {
  const parts = [
    s.last_newsletter_at !== null ? `Last: ${fmtDay(s.last_newsletter_at)}` : null,
    s.next_newsletter_at !== null ? `Next: ${fmtWhen(s.next_newsletter_at)}` : null,
    s.newsletter_unsubscribed_at !== null
      ? `Unsubscribed from a newsletter's link ${fmtDay(s.newsletter_unsubscribed_at)}`
      : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? undefined : parts.join(" · ");
}

function SubscriberRow({ s }: { readonly s: NewsletterSubscriber }): JSX.Element {
  return (
    <tr>
      <td className="table-card-title">
        <Link to={`/members/${s.member_id}`}>{s.display_name ?? s.email ?? "(no name)"}</Link>
      </td>
      <td data-label="Email">{s.email ?? "—"}</td>
      <td data-label="Joined">{fmtDay(s.joined_at)}</td>
      <td data-label="How" title={JOINED_VIA_HINT[s.joined_via]}>
        {JOINED_VIA_LABEL[s.joined_via]}
      </td>
      <td data-label="Status">
        {s.on_list ? (
          <span className="tag tag-success">On the list</span>
        ) : (
          <span className="tag" title={s.left_out_reason ?? undefined}>
            {s.left_out_reason === null ? "Left out" : exclusionLabel(s.left_out_reason, "email")}
          </span>
        )}
      </td>
      <td data-label="Lifecycle · goal">
        {LIFECYCLE_LABEL[s.lifecycle]} · {GOAL_LABEL[s.goal]}
      </td>
      <td data-label="Newsletters" title={newslettersTitle(s)}>
        {newslettersCell(s)}
        {s.next_newsletter_at !== null && <div className="subtle">Next {fmtDay(s.next_newsletter_at)}</div>}
      </td>
      <td data-label="Welcome email">{fmtDate(s.welcome_sent_at)}</td>
      <td
        data-label="WhatsApp"
        title={
          s.last_whatsapp_newsletter_at !== null
            ? `Last WhatsApp newsletter ${fmtDay(s.last_whatsapp_newsletter_at)}`
            : undefined
        }
      >
        {s.whatsapp_reachable ? "Reachable" : "Not reachable"}
        {s.whatsapp_newsletters_sent > 0 && (
          <div className="subtle">{fmtCount(s.whatsapp_newsletters_sent)} sent</div>
        )}
      </td>
    </tr>
  );
}

function SubscribersTab(): JSX.Element {
  const [view, setView] = useState<NewsletterSubscriberView>("on_list");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [list, setList] = useState<NewsletterSubscribersList | null>(null);
  const [rows, setRows] = useState<ReadonlyArray<NewsletterSubscriber>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Only the latest read may land: a filter or search changed while a read
  // was in flight makes its answer stale.
  const readSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim()), SUBSCRIBER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const read = useCallback(
    (offset: number): Promise<void> => {
      const seq = ++readSeq.current;
      const input: NewsletterSubscribersInput = {
        view,
        limit: SUBSCRIBERS_PAGE,
        offset,
        ...(search.length > 0 ? { search } : {}),
      };
      return dispatchAction<NewsletterSubscribersInput, NewsletterSubscribersList>(
        "list_newsletter_subscribers",
        input,
      )
        .then((res) => {
          if (seq !== readSeq.current) return;
          if (res.ok) {
            setList(res.response);
            setRows((prev) =>
              offset === 0 ? res.response.subscribers : appendSubscriberPage(prev, res.response.subscribers),
            );
            setError(null);
          } else {
            setError(res.message);
          }
        })
        .catch(() => {
          if (seq === readSeq.current) setError(UNREACHABLE);
        });
    },
    [view, search],
  );

  useEffect(() => {
    setList(null);
    setRows([]);
    void read(0);
  }, [read]);

  const nextOffset = list === null ? null : list.next_offset;
  const loadMore = (): void => {
    if (nextOffset === null || loadingMore) return;
    setLoadingMore(true);
    void read(nextOffset).finally(() => setLoadingMore(false));
  };

  return (
    <>
      <p className="member-note">
        Who the email newsletter goes to, newest joiner first. It goes to every active member it can
        reach, so most never signed up: they joined as members. Open a name for their page, where the
        Journey shows each email they received.
      </p>
      {error !== null && <div className="tag tag-error campaigns-notice">{error}</div>}
      <div className="stat-grid">
        {subscriberTiles(
          list?.totals ?? {
            active_members: 0,
            on_list: 0,
            left_out: 0,
            signed_up: 0,
            joined_last_7_days: 0,
            joined_last_30_days: 0,
          },
        ).map((t) => (
          <div key={t.label} className="stat-card" title={t.hint}>
            <div className="label">{t.label}</div>
            <div className="value">{list === null ? "—" : fmtCount(t.value)}</div>
          </div>
        ))}
      </div>
      <section className="panel">
        <h2>Subscribers</h2>
        <div className="campaigns-controls">
          <label>
            Show{" "}
            <select
              value={view}
              onChange={(e) => {
                const next = NEWSLETTER_SUBSCRIBER_VIEWS.find((v) => v === e.target.value);
                if (next !== undefined) setView(next);
              }}
            >
              {NEWSLETTER_SUBSCRIBER_VIEWS.map((v) => (
                <option key={v} value={v}>
                  {SUBSCRIBER_VIEW_LABEL[v]}
                </option>
              ))}
            </select>
          </label>
          <input
            type="search"
            className="campaigns-subscriber-search"
            placeholder="Search a name or email"
            value={searchDraft}
            maxLength={100}
            onChange={(e) => setSearchDraft(e.target.value)}
            aria-label="Search subscribers"
          />
        </div>
        {list === null && error === null && <p className="empty">Loading…</p>}
        {list !== null && rows.length === 0 && (
          <p className="empty">{search.length > 0 ? "Nobody matches that search." : "Nobody here yet."}</p>
        )}
        {rows.length > 0 && (
          <div className="table-scroll-x">
            <table className="campaigns-subscribers table-cards">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Joined</th>
                  <th>How</th>
                  <th>Status</th>
                  <th>Lifecycle · goal</th>
                  <th>Newsletters</th>
                  <th>Welcome email</th>
                  <th>WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <SubscriberRow key={s.member_id} s={s} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list !== null && (
          <p className="member-note subtle">
            {fmtCount(rows.length)} of {fmtCount(list.matched)} shown. As of {fmtWhen(list.as_of)}.
            Newsletters counts the ones Clavis sent; who received the older ones sent by hand from
            Resend is only on Resend. Opens over-count (Apple Mail opens every email it delivers).
          </p>
        )}
        {list !== null && nextOffset !== null && (
          <div className="campaigns-subscribers-more">
            <button type="button" className="action-dispatch-btn" disabled={loadingMore} onClick={loadMore}>
              {loadingMore
                ? "Loading…"
                : `Load ${fmtCount(Math.min(SUBSCRIBERS_PAGE, list.matched - rows.length))} more`}
            </button>
          </div>
        )}
      </section>
    </>
  );
}

export function Campaigns(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tab = tabFrom(params.get("tab"));
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  const select = (next: Tab, focus: boolean): void => {
    setParams(next === "newsletters" ? {} : { tab: next }, { replace: true });
    if (focus) tabRefs.current[next]?.focus();
  };

  // The tablist pattern (`tabKeyTarget`): the arrow keys move between the
  // tabs and open the one they land on, Home and End jump to the ends, a key
  // with a modifier held is left to the browser, and Tab leaves for the open
  // panel — only the open tab is in the tab order (2026-10-18 review).
  const onTabKey = (ev: ReactKeyboardEvent<HTMLDivElement>): void => {
    const to = tabKeyTarget(ev, TABS.findIndex((t) => t.value === tab), TABS.length);
    const next = to === null ? undefined : TABS[to];
    if (next === undefined) return;
    ev.preventDefault();
    select(next.value, true);
  };

  return (
    <div className="page-campaigns">
      <h1 className="page-title">Campaigns</h1>
      <div className="pr-type-tabs campaigns-tabs" role="tablist" aria-label="Campaigns" onKeyDown={onTabKey}>
        {TABS.map((t) => (
          <button
            key={t.value}
            ref={(el) => {
              tabRefs.current[t.value] = el;
            }}
            type="button"
            role="tab"
            id={`campaigns-tab-${t.value}`}
            aria-selected={tab === t.value}
            aria-controls={tab === t.value ? `campaigns-panel-${t.value}` : undefined}
            tabIndex={tab === t.value ? 0 : -1}
            className={`pr-type-tab${tab === t.value ? " active" : ""}`}
            onClick={() => select(t.value, false)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`campaigns-panel-${tab}`} aria-labelledby={`campaigns-tab-${tab}`}>
        {tab === "newsletters" && <NewslettersTab />}
        {tab === "editions" && <EditionsTab />}
        {tab === "audiences" && <AudiencesTab />}
        {tab === "subscribers" && <SubscribersTab />}
      </div>
    </div>
  );
}
